/**
 * Approves — the manager's proposals waiting on the owner.
 *
 * A manager drives the whole admin app, but their changes do not take effect: every write is captured as a change
 * request (services/changeRequests) for the owner to approve, which then replays it verbatim as an admin. The API
 * for that has been live since the manager hat shipped — but no page was ever built for it, so `/approves` and
 * `/change-requests` were "Page not found" and the queue was invisible. Proposals piled up where nobody could see
 * or act on them (audits of Sep 16 and Sep 19, P0 both times).
 *
 * Approving replays the original request through the same handler a direct admin edit would use, so nothing here
 * has its own idea of what a change means. Rejecting drops it and tells the manager nothing on its own.
 *
 * Since Nov 2 2026 the same queue also sits at the top of Needs Attention ("Waiting on your OK") — the rows, the
 * buttons and the fetch live in components/ApprovalsQueue, so this page and that block can never disagree.
 */
import React from 'react';
import { Inbox, AlertTriangle } from 'lucide-react';
import { ApprovalRows, ApproveAllButton, useApprovalsQueue, whyNotLoaded } from '../components/ApprovalsQueue';

export default function Approves() {
  const queue = useApprovalsQueue();
  const { requests, isLoading, isError, error } = queue;
  const why = whyNotLoaded(error);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Approves</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Changes a manager asked for. Nothing here has happened yet — approving runs it exactly as if you had done it yourself.
          </p>
        </div>
        <ApproveAllButton queue={queue} />
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {isError && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            <b>{why.title}.</b> {why.body}
          </span>
        </div>
      )}

      {!isLoading && !isError && requests.length === 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white px-5 py-10 text-center">
          <Inbox className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm font-semibold text-gray-700">Nothing waiting</p>
          <p className="text-xs text-gray-500 mt-1">When a manager changes something, it lands here for you first.</p>
        </div>
      )}

      <ApprovalRows queue={queue} />
    </div>
  );
}
